/**
 * Cyphex Teams — normative media contract.
 *
 * This module is the single source of truth for every media decision made by
 * apps/web, apps/api and apps/gateway. Derived in docs/CAPACITY-EX44.md; do
 * not change a number here without re-running that study, because the box's
 * 300-participant design point depends on these exact values.
 */

/** Simulcast layer identifiers, cheapest first. */
export type MediaLayer = 'l0_180p' | 'l1_360p' | 'l2_540p' | 'l3_720p' | 'l4_1080p';

/** Quality profile assigned at token mint by apps/api admission control. */
export type QualityProfile = 'standard' | 'dense' | 'low-bandwidth' | 'audio-only';

/** Global egress pressure state driving the overload ladder. */
export type LoadState = 'green' | 'amber' | 'red' | 'critical';

export interface LayerSpec {
  readonly width: number;
  readonly height: number;
  readonly fps: number;
  /** Encoder target, bits per second. */
  readonly bitrate: number;
}

export interface ProfileSpec {
  /** Highest layer any spotlight/pinned tile may request. */
  readonly maxSpotlightLayer: MediaLayer;
  /** Highest layer a grid tile may request. */
  readonly maxGridLayer: MediaLayer;
  /** Highest layer a non-speaking grid tile may request in density mode. */
  readonly maxSecondaryLayer: MediaLayer;
  /** Screen-share encoder ceiling, bps. */
  readonly screenShareBitrate: number;
  /** Screen-share frame rate for `contentHint: 'detail'`. */
  readonly screenShareFps: number;
  /** Number of remote audio tracks to subscribe to (top-N active speakers). */
  readonly audioSubscriptions: number;
  /** Whether remote video is subscribed at all. */
  readonly videoEnabled: boolean;
}

/** Encoder ladder. On-the-wire cost is ~1.10x these figures (see study §1.1). */
export const LAYER_SPECS: Readonly<Record<MediaLayer, LayerSpec>> = {
  l0_180p: { width: 320, height: 180, fps: 15, bitrate: 150_000 },
  l1_360p: { width: 640, height: 360, fps: 20, bitrate: 500_000 },
  l2_540p: { width: 960, height: 540, fps: 25, bitrate: 800_000 },
  l3_720p: { width: 1280, height: 720, fps: 30, bitrate: 1_700_000 },
  l4_1080p: { width: 1920, height: 1080, fps: 30, bitrate: 3_000_000 },
} as const;

/**
 * The three-rung simulcast ladder every camera publishes. Dynacast pauses any
 * rung nobody is watching, so publishing all three costs nothing when the room
 * only wants 360p.
 */
export const SIMULCAST_LADDER: readonly MediaLayer[] = ['l0_180p', 'l1_360p', 'l3_720p'] as const;

export const QUALITY_PROFILES: Readonly<Record<QualityProfile, ProfileSpec>> = {
  standard: {
    maxSpotlightLayer: 'l3_720p',
    maxGridLayer: 'l1_360p',
    maxSecondaryLayer: 'l0_180p',
    screenShareBitrate: 1_500_000,
    screenShareFps: 15,
    audioSubscriptions: 3,
    videoEnabled: true,
  },
  dense: {
    maxSpotlightLayer: 'l1_360p',
    maxGridLayer: 'l0_180p',
    maxSecondaryLayer: 'l0_180p',
    screenShareBitrate: 1_200_000,
    screenShareFps: 10,
    audioSubscriptions: 3,
    videoEnabled: true,
  },
  'low-bandwidth': {
    maxSpotlightLayer: 'l0_180p',
    maxGridLayer: 'l0_180p',
    maxSecondaryLayer: 'l0_180p',
    screenShareBitrate: 1_000_000,
    screenShareFps: 8,
    audioSubscriptions: 2,
    videoEnabled: true,
  },
  'audio-only': {
    maxSpotlightLayer: 'l0_180p',
    maxGridLayer: 'l0_180p',
    maxSecondaryLayer: 'l0_180p',
    screenShareBitrate: 0,
    screenShareFps: 0,
    audioSubscriptions: 3,
    videoEnabled: false,
  },
} as const;

/**
 * Hard limits. These are enforced twice: client-side for correct UX, and
 * server-side (LiveKit `limit:` block + apps/api admission control) so that a
 * buggy or hostile client cannot bypass them.
 */
export const MEDIA_LIMITS = {
  /** Never render more than this many video tiles; paginate beyond it. */
  MAX_VISIBLE_TILES: 9,
  /** Above this tile count, non-speaker tiles drop to maxSecondaryLayer. */
  DENSITY_MODE_THRESHOLD: 5,
  /** Room participant caps. */
  ROOM_SOFT_CAP: 250,
  ROOM_HARD_CAP: 300,
  /** Above this, the room enters town-hall mode (subscribe-only audience). */
  TOWN_HALL_THRESHOLD: 50,
  /** Simultaneous video publishers allowed. */
  MAX_PUBLISHERS_NORMAL: 16,
  MAX_PUBLISHERS_TOWN_HALL: 9,
  /** Server-side subscription backstops, mirrored in livekit.yaml. */
  MAX_VIDEO_SUBSCRIPTIONS: 9,
  MAX_AUDIO_SUBSCRIPTIONS: 5,
  /** Per-participant publish ceiling, bps (livekit.yaml bytes_per_sec x 8). */
  MAX_PUBLISH_BITRATE: 4_000_000,
  /** A single room may not exceed this share of the uplink, bps. */
  MAX_ROOM_EGRESS: 250_000_000,
  /** Seconds a tab may be hidden before remote video is disabled. */
  HIDDEN_TAB_VIDEO_TIMEOUT_S: 10,
  /** LiveKit join-token lifetime, seconds. */
  JOIN_TOKEN_TTL_S: 300,
} as const;

/** Global egress thresholds in bps, driving the overload ladder (study §7). */
export const LOAD_THRESHOLDS: Readonly<Record<Exclude<LoadState, 'green'>, number>> = {
  amber: 550_000_000,
  red: 750_000_000,
  critical: 900_000_000,
} as const;

/** Codec preference, best-first. VP8 is the universal fallback. */
export const CODEC_PREFERENCE: readonly string[] = ['av1', 'vp9', 'vp8', 'h264'] as const;

/** Opus publish settings. DTX + FEC are mandatory (study §4.1). */
export const AUDIO_SETTINGS = {
  channelCount: 1,
  maxBitrate: 32_000,
  dtx: true,
  red: true,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
} as const;
